/** Mappers PocketBase (snake_case) → domaine (camelCase). */
import {
  type Consumption,
  deriveMacroTargets,
  type Exercise,
  type Food,
  type FoodComposition,
  type Meal,
  type Milestone,
  PROTEIN_PER_KG,
  type Profile,
  suggestProteinTarget,
  suggestWaterTarget,
  WATER_ML_PER_KG,
  type WaterEntry,
  type WeightEntry,
} from '@/domain';
import type {
  ConsumptionRecord,
  ExerciseEntryRecord,
  FoodRecord,
  MilestoneRecord,
  ProfileRecord,
  WaterEntryRecord,
  WeightEntryRecord,
} from '@/types/records';

/** Normalise une date PocketBase (« 2026-07-10 00:00:00.000Z ») en ISO date. */
function toIsoDate(pbDate: string): string {
  return pbDate.slice(0, 10);
}

export function mapProfile(r: ProfileRecord): Profile {
  // Rétro-compat : champs protéine absents → suggestion par défaut (poids inconnu
  // ici, recalculée à l'affichage depuis la dernière pesée).
  const proteinPerKg = r.protein_per_kg || PROTEIN_PER_KG.default;
  const proteinTargetG = r.protein_target_g || suggestProteinTarget(70, proteinPerKg);
  const fatPct = r.fat_pct ?? 30;
  // Objectifs G/L dérivés du seuil si absents.
  const derived = deriveMacroTargets(r.calorie_threshold, proteinTargetG, fatPct);
  return {
    birthdate: toIsoDate(r.birthdate),
    heightCm: r.height_cm,
    sex: r.sex,
    activityLevel: r.activity_level,
    idealWeightKg: r.ideal_weight_kg,
    targetWeightKg: r.target_weight_kg,
    calorieThreshold: r.calorie_threshold,
    thresholdMode: r.threshold_mode,
    proteinTargetG,
    proteinTargetMode: r.protein_target_mode ?? 'auto',
    proteinPerKg,
    emailReminders: r.email_reminders ?? false,
    pushReminders: r.push_reminders ?? false,
    macroTargetMode: r.macro_target_mode ?? 'auto',
    fatPct,
    carbTargetG: r.carb_target_g || derived.carbG,
    fatTargetG: r.fat_target_g || derived.fatG,
    waterMlPerKg: r.water_ml_per_kg || WATER_ML_PER_KG.default,
    waterTargetMode: r.water_target_mode ?? 'auto',
    waterTargetMl:
      r.water_target_ml || suggestWaterTarget(70, r.water_ml_per_kg || WATER_ML_PER_KG.default),
    // Rétro-compat : 0.5 kg/sem par défaut.
    weeklyLossKg: r.weekly_loss_kg || 0.5,
    // `||` (pas `??`) : un select PB vide renvoie '' (falsy non-nullish) → 'cut'.
    trainingPhase: r.training_phase || 'cut',
    // Rétro-compat : +350 kcal/j par défaut (milieu de la bande sourcée).
    surplusKcal: r.surplus_kcal || 350,
  };
}

export function mapWater(r: WaterEntryRecord): WaterEntry {
  return { datetime: r.datetime.replace(' ', 'T'), ml: r.ml };
}

export function mapExercise(r: ExerciseEntryRecord): Exercise {
  return {
    datetime: r.datetime.replace(' ', 'T'),
    activity: r.activity,
    kcalBurned: r.kcal_burned,
    durationMin: r.duration_min,
    note: r.note,
  };
}

export function mapWeight(r: WeightEntryRecord): WeightEntry {
  return { date: toIsoDate(r.date), weightKg: r.weight_kg };
}

export function mapMilestone(r: MilestoneRecord): Milestone {
  return {
    targetWeightKg: r.target_weight_kg,
    label: r.label,
    achievedAt: r.achieved_at ? toIsoDate(r.achieved_at) : null,
  };
}

function mapComposition(r: FoodRecord): FoodComposition {
  return {
    kcal100g: r.kcal_100g,
    protein100g: r.protein_100g,
    carbs100g: r.carbs_100g,
    sugars100g: r.sugars_100g,
    fat100g: r.fat_100g,
    saturated100g: r.saturated_100g,
    fiber100g: r.fiber_100g,
    salt100g: r.salt_100g,
  };
}

export function mapFood(r: FoodRecord): Food {
  return {
    id: r.id,
    name: r.name,
    isPublic: r.isPublic,
    ...mapComposition(r),
  };
}

/** Nécessite l'aliment développé (`expand: food`). */
export function mapConsumption(r: ConsumptionRecord): Consumption {
  const food = r.expand?.food;
  if (!food) throw new Error(`Consommation ${r.id} sans aliment développé`);
  return {
    // PocketBase renvoie un datetime avec un espace ; « T » pour un ISO strict (parseISO).
    datetime: r.datetime.replace(' ', 'T'),
    grams: r.grams,
    food: mapComposition(food),
    meal: r.meal ? (r.meal as Meal) : undefined,
  };
}
